import { useTranslation } from 'next-i18next'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { Link } from '@src/quince-components/src/atoms/link'
import { setFindingMethod, FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_ACIONS } from '@src/telemetry/telemetry-events.constants'

import styles from './header-link-scrolling-menu.module.scss'

import type { HeaderLinkScrollingMenuWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * HEADER_LINK_SCROLLING_MENU widget (non-DS / legacy path).
 *
 * The mobile-web quick-link menu that used to render as the legacy header's tertiary-menu row (the
 * `shouldRenderTertiaryMenus` block in header-legacy.tsx), now BE-driven: a horizontally scrolling
 * row of category links at the top of the home PL page. Telemetry mirrors the header row it
 * replaces (`click_header_quick_link` + the NAVIGATION finding-method), so dashboards keyed on that
 * action keep working across the migration. Self-contained; data comes from
 * `data.headerLinkScrollingMenuData`.
 */
export function HeaderLinkScrollingMenuWidgetLegacy(props: PLTypedWidgetProps<HeaderLinkScrollingMenuWidgetData>) {
  const { t } = useTranslation('common')
  // `data.analytics` (generic content-container tracking) is intentionally unused: the legacy
  // header row it replaces has per-link click tracking only, replicated below. Wire it through
  // `buildWidgetTrackingMetaData` if the BE starts populating it.
  const data = props.data.headerLinkScrollingMenuData
  const items = data?.items ?? []
  // `enabled` is wire-trusted (proto keeps it for back-compat during rollout): absent/false ⇒ hidden.
  if (!data?.enabled || items.length === 0) return null

  return (
    <nav className={styles.menuContainer} data-testid="header-link-scrolling-menu-widget">
      {items.map((item, index) => (
        <Link
          href={item.slug}
          className={styles.menuLink}
          enableEarlyFetch={false}
          key={item.id || item.slug}
          onClick={() => {
            setFindingMethod[FINDING_METHOD_AGGREGATE.NAVIGATION]?.({
              L1: item.text,
            })
          }}
          trackConfig={{
            trackGA: true,
            trackInternal: true,
            category: t('navigation'),
            action: TELEMETRY_EVENT_ACIONS.CLICK_HEADER_QUICK_LINK,
            value: index,
            label: item.text,
          }}
          style={item.textColor ? { color: item.textColor } : undefined}
        >
          {item.text}
        </Link>
      ))}
    </nav>
  )
}
